import React, { useCallback, useContext, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { ChatHeaderSkeleton } from "../Components/inbox/InboxSkeletons";

import { RoleContext } from "../Contexts/RoleContext";
import { socket } from "../socket";
import useInboxSocket from "../Components/inbox/useInboxSocket";
import {
  getConversations,
  getConversation as fetchConversation,
  getMessages,
  sendMessage as apiSendMessage,
  sendInternalNote as apiSendNote,
  updateConversationStatus,
  toggleStar as apiToggleStar,
  updateTags as apiUpdateTags,
  assignConversation as apiAssign,
  getTemplates,
  getChannelAccounts,
} from "../Components/inbox/inboxApi";
import {
  disconnectWhatsAppAccount,
  getWhatsAppOAuthUrl,
  getWhatsAppStatus,
  listWhatsAppAccounts,
} from "../Components/whatsapp/whatsappApi";
import { url as apiBaseUrl } from "../Components/url";
import ConversationList from "../Components/inbox/ConversationList";
import ChatWindow from "../Components/inbox/ChatWindow";
import MessageInput from "../Components/inbox/MessageInput";
import AISuggestions from "../Components/inbox/AISuggestions";
import InboxChannelSetupModal from "../Components/inbox/InboxChannelSetupModal";

const DEFAULT_BUSINESS_ID = Number(process.env.REACT_APP_DEFAULT_BUSINESS_ID || 1);

export default function UnifiedInboxPage() {
  const queryClient = useQueryClient();
  const { role } = useContext(RoleContext);
  const normalizedRole = (role || "").trim().toLowerCase();
  const canConfigureChannels = normalizedRole === "admin" || normalizedRole === "manager";

  const [activeId, setActiveId] = useState(null);
  const [channelSetupOpen, setChannelSetupOpen] = useState(false);
  const [composer, setComposer] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [waNotice, setWaNotice] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useInboxSocket(DEFAULT_BUSINESS_ID, activeId);

  useEffect(() => {
    function onTyping(data) {
      if (data.conversation_id !== activeId) return;
      if (data.typing) {
        setTypingUser(data.username);
        setTimeout(() => setTypingUser(null), 3000);
      } else {
        setTypingUser(null);
      }
    }
    socket.on("inbox:typing", onTyping);
    return () => socket.off("inbox:typing", onTyping);
  }, [activeId]);

  const convsQuery = useQuery({
    queryKey: ["inbox-conversations", statusFilter, channelFilter, debouncedSearch],
    queryFn: () =>
      getConversations({
        businessId: DEFAULT_BUSINESS_ID,
        status: statusFilter || undefined,
        channel: channelFilter || undefined,
        search: debouncedSearch || undefined,
        page: 1,
        perPage: 50,
      }),
    refetchInterval: 15000,
  });
  const conversations = convsQuery.data?.items || [];

  const waStatusQuery = useQuery({
    queryKey: ["whatsapp-status", DEFAULT_BUSINESS_ID],
    queryFn: () => getWhatsAppStatus(DEFAULT_BUSINESS_ID),
    staleTime: 60_000,
  });
  const channelAccQuery = useQuery({
    queryKey: ["inbox-channel-accounts"],
    queryFn: () => getChannelAccounts(),
    staleTime: 60_000,
  });
  const waAccountsQuery = useQuery({
    queryKey: ["whatsapp-accounts"],
    queryFn: () => listWhatsAppAccounts(),
    staleTime: 60_000,
  });

  const channelsLoading = waStatusQuery.isLoading || channelAccQuery.isLoading;
  const channelItems = channelAccQuery.data?.items || [];
  const whatsappOk =
    waStatusQuery.data?.connected === true ||
    channelItems.some((a) => a.channel_type === "whatsapp" && a.is_active);
  /** Empty-state “configure” prompt: WhatsApp is the default external medium for this inbox. */
  const unconfiguredMediums = whatsappOk ? [] : ["WhatsApp"];
  const waAccounts = waAccountsQuery.data?.items || [];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("wa_connect");
    if (!status) return;
    if (status === "success") {
      setWaNotice("WhatsApp account connected successfully.");
      queryClient.invalidateQueries({ queryKey: ["whatsapp-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-status", DEFAULT_BUSINESS_ID] });
    }
    if (status === "error") setWaNotice(`WhatsApp connection failed: ${params.get("message") || "Unknown error"}`);
    params.delete("wa_connect");
    params.delete("message");
    const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
    window.history.replaceState({}, document.title, next);
  }, [queryClient]);

  useEffect(() => {
    if (channelsLoading || activeId || !conversations.length) return;
    if (!whatsappOk) return;
    setActiveId(conversations[0].id);
  }, [channelsLoading, conversations, activeId, whatsappOk]);

  const activeConvQuery = useQuery({
    queryKey: ["inbox-conversation", activeId],
    queryFn: () => fetchConversation(activeId),
    enabled: !!activeId,
  });
  const activeConv = activeConvQuery.data?.conversation || null;

  const msgsQuery = useQuery({
    queryKey: ["inbox-messages", activeId],
    queryFn: () => getMessages(activeId, { page: 1, perPage: 100 }),
    enabled: !!activeId,
  });
  const messages = msgsQuery.data?.items || [];

  const tplQuery = useQuery({
    queryKey: ["inbox-templates"],
    queryFn: () => getTemplates(),
    staleTime: 60_000,
  });
  const templates = tplQuery.data?.items || [];

  const counsellorsQuery = useQuery({
    queryKey: ["inbox-counsellors"],
    queryFn: async () => {
      const { fetchWithAuth } = await import("../Components/FetchWithAuth");
      const { url } = await import("../Components/url");
      const r = await fetchWithAuth(`${url}/api/whatsapp-chat/counsellors`, { method: "GET" });
      return r.json().catch(() => ({ items: [] }));
    },
    staleTime: 120_000,
  });
  const counsellors = counsellorsQuery.data?.items || [];

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["inbox-conversations"] });
    if (activeId) {
      queryClient.invalidateQueries({ queryKey: ["inbox-messages", activeId] });
      queryClient.invalidateQueries({ queryKey: ["inbox-conversation", activeId] });
    }
  }, [queryClient, activeId]);

  const handleSend = useCallback(
    async ({ content, channel }) => {
      if (!activeId) return;
      try {
        setSaving(true);
        await apiSendMessage({
          conversation_id: activeId,
          content,
          channel,
        });
        setComposer("");
        invalidateAll();
      } finally {
        setSaving(false);
      }
    },
    [activeId, invalidateAll]
  );

  const handleNote = useCallback(
    async (content) => {
      if (!activeId) return;
      try {
        setSaving(true);
        await apiSendNote({ conversation_id: activeId, content });
        setComposer("");
        invalidateAll();
      } finally {
        setSaving(false);
      }
    },
    [activeId, invalidateAll]
  );

  const handleStatusChange = useCallback(
    async (status) => {
      if (!activeId) return;
      await updateConversationStatus(activeId, status);
      invalidateAll();
    },
    [activeId, invalidateAll]
  );

  const handleToggleStar = useCallback(async () => {
    if (!activeId) return;
    await apiToggleStar(activeId);
    invalidateAll();
  }, [activeId, invalidateAll]);

  const handleUpdateTags = useCallback(
    async (tags) => {
      if (!activeId) return;
      await apiUpdateTags(activeId, tags);
      invalidateAll();
    },
    [activeId, invalidateAll]
  );

  const handleAssign = useCallback(
    async (staffId) => {
      if (!activeId) return;
      await apiAssign(activeId, staffId);
      invalidateAll();
    },
    [activeId, invalidateAll]
  );

  const tags = activeConv?.tags || [];

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || tags.includes(t)) return;
    handleUpdateTags([...tags, t]);
    setTagInput("");
  };

  const removeTag = (tag) => {
    handleUpdateTags(tags.filter((x) => x !== tag));
  };

  const startOAuthConnect = async () => {
    const data = await getWhatsAppOAuthUrl();
    if (!data?.auth_url) {
      setWaNotice(data?.error || "Unable to start WhatsApp connection.");
      return;
    }
    window.open(data.auth_url, "wa_oauth", "width=700,height=780,noopener,noreferrer");
  };

  const handleDisconnectAccount = async (accountId) => {
    await disconnectWhatsAppAccount(accountId);
    setWaNotice("WhatsApp account disconnected.");
    queryClient.invalidateQueries({ queryKey: ["whatsapp-accounts"] });
    queryClient.invalidateQueries({ queryKey: ["whatsapp-status", DEFAULT_BUSINESS_ID] });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#fff" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          borderBottom: "1px solid #e2e8f0",
          background: "#faf5ff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InboxOutlinedIcon sx={{ fontSize: 22, color: "#7c3aed" }} />
          <span style={{ fontWeight: 700, fontSize: 16 }}>Unified Inbox</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            onClick={startOAuthConnect}
            style={{
              border: "none",
              borderRadius: 8,
              background: "#25d366",
              color: "#fff",
              fontWeight: 700,
              padding: "6px 12px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 18 }} />
            Connect WhatsApp
          </button>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: "2px 10px",
              borderRadius: 12,
              background: "#ede9fe",
              color: "#5b21b6",
            }}
          >
            {role || "staff"}
          </span>
        </div>
      </div>
      {waNotice ? (
        <div
          style={{
            padding: "8px 16px",
            fontSize: 12,
            color: "#4c1d95",
            background: "#f5f3ff",
            borderBottom: "1px solid #e9d5ff",
          }}
        >
          {waNotice}
        </div>
      ) : null}

      {/* Two columns: chat (left) | contact list (right) — no middle panel */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>
        {/* Left: conversation thread + compose */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            background: "#fff",
          }}
        >
          {activeId && activeConvQuery.isLoading && !activeConv && <ChatHeaderSkeleton />}

          {activeConv && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "10px 16px",
                  borderBottom: "1px solid #e2e8f0",
                  flexShrink: 0,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {activeConv.display_name || activeConv.contact_identifier}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    {activeConv.contact_identifier}
                    {activeConv.status && (
                      <span
                        style={{
                          marginLeft: 8,
                          padding: "1px 6px",
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          background: "#ede9fe",
                          color: "#5b21b6",
                          textTransform: "capitalize",
                        }}
                      >
                        {activeConv.status}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={handleToggleStar}
                    title="Star"
                    style={{ border: "none", background: "none", cursor: "pointer", padding: 4, lineHeight: 0 }}
                  >
                    {activeConv.is_starred ? (
                      <StarIcon sx={{ fontSize: 22, color: "#f59e0b" }} />
                    ) : (
                      <StarBorderIcon sx={{ fontSize: 22, color: "#94a3b8" }} />
                    )}
                  </button>
                  <select
                    value={activeConv.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    style={{ fontSize: 12, padding: "4px 8px", borderRadius: 6, border: "1px solid #e2e8f0" }}
                  >
                    <option value="open">Open</option>
                    <option value="pending">Pending</option>
                    <option value="closed">Closed</option>
                    <option value="snoozed">Snoozed</option>
                  </select>
                  {counsellors.length > 0 && (
                    <select
                      value={activeConv.assigned_to || ""}
                      onChange={(e) => handleAssign(e.target.value ? Number(e.target.value) : null)}
                      style={{ fontSize: 12, padding: "4px 8px", borderRadius: 6, border: "1px solid #e2e8f0", minWidth: 120 }}
                    >
                      <option value="">Unassigned</option>
                      {counsellors.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.first_name} {c.last_name}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    type="button"
                    onClick={() => handleStatusChange("closed")}
                    disabled={activeConv.status === "closed"}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      border: "1px solid #e2e8f0",
                      borderRadius: 6,
                      padding: "4px 8px",
                      fontSize: 12,
                      cursor: activeConv.status === "closed" ? "default" : "pointer",
                      background: "#f0fdf4",
                      color: "#166534",
                      opacity: activeConv.status === "closed" ? 0.5 : 1,
                    }}
                  >
                    <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                    Resolve
                  </button>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 16px",
                  borderBottom: "1px solid #e2e8f0",
                  flexWrap: "wrap",
                  flexShrink: 0,
                }}
              >
                <LocalOfferOutlinedIcon sx={{ fontSize: 16, color: "#64748b", flexShrink: 0 }} />
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, flex: 1, minWidth: 0 }}>
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "2px 8px",
                        borderRadius: 12,
                        background: "#ede9fe",
                        color: "#5b21b6",
                        fontSize: 12,
                      }}
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        style={{ border: "none", background: "none", cursor: "pointer", padding: 0, color: "#5b21b6", fontWeight: 700 }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Add tag"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addTag()}
                  style={{ width: 100, fontSize: 12, padding: "4px 8px", borderRadius: 6, border: "1px solid #e2e8f0" }}
                />
                <button
                  type="button"
                  onClick={addTag}
                  style={{
                    fontSize: 12,
                    padding: "4px 10px",
                    borderRadius: 6,
                    border: "none",
                    background: "#7c3aed",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Add
                </button>
              </div>
            </>
          )}

          <ChatWindow
            messages={messages}
            isLoading={Boolean(activeId && msgsQuery.isLoading)}
            typingUser={typingUser}
            conversationSelected={Boolean(activeId)}
            channelsLoading={channelsLoading}
            unconfiguredMediums={unconfiguredMediums}
            onOpenChannelSetup={() => setChannelSetupOpen(true)}
            canConfigureChannels={canConfigureChannels}
          />

          <InboxChannelSetupModal
            open={channelSetupOpen}
            onClose={() => setChannelSetupOpen(false)}
            businessId={DEFAULT_BUSINESS_ID}
            apiBaseUrl={apiBaseUrl}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ["whatsapp-status", DEFAULT_BUSINESS_ID] });
              queryClient.invalidateQueries({ queryKey: ["inbox-channel-accounts"] });
              queryClient.invalidateQueries({ queryKey: ["whatsapp-accounts"] });
              queryClient.invalidateQueries({ queryKey: ["inbox-conversations"] });
            }}
          />

          {activeId && (
            <>
              <AISuggestions
                conversationId={activeId}
                autoReplyEnabled={activeConv?.auto_reply_enabled}
                onInsert={(text) => setComposer(text)}
                onAutoReplyToggle={invalidateAll}
              />
              <MessageInput
                onSend={handleSend}
                onSendNote={handleNote}
                defaultChannel={activeConv?.last_channel || "whatsapp"}
                disabled={saving}
                templates={templates}
                composerValue={composer}
                onComposerChange={setComposer}
              />
            </>
          )}
        </div>

        {/* Right: contacts / conversation list */}
        <div style={{ width: 320, minWidth: 260, flexShrink: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ borderLeft: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0", padding: 10 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Connected WhatsApp accounts</span>
              <button
                type="button"
                onClick={startOAuthConnect}
                style={{
                  fontSize: 11,
                  border: "none",
                  background: "transparent",
                  color: "#7c3aed",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Reconnect
              </button>
            </div>
            {waAccountsQuery.isLoading ? (
              <span style={{ fontSize: 12, color: "#94a3b8" }}>Loading…</span>
            ) : waAccounts.length === 0 ? (
              <span style={{ fontSize: 12, color: "#94a3b8" }}>No WhatsApp account connected</span>
            ) : (
              waAccounts.map((acc) => (
                <div
                  key={acc.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginTop: 6,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#0f172a",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {acc.display_phone_number || acc.phone_number_id}
                    </div>
                    <div style={{ fontSize: 11, color: acc.is_active ? "#15803d" : "#a16207" }}>
                      {acc.is_active ? "Connected" : "Inactive"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDisconnectAccount(acc.id)}
                    style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", lineHeight: 0 }}
                    title="Disconnect"
                  >
                    <LinkOffOutlinedIcon sx={{ fontSize: 18 }} />
                  </button>
                </div>
              ))
            )}
          </div>
          <ConversationList
            conversations={conversations}
            isLoading={convsQuery.isLoading}
            activeId={activeId}
            onSelect={setActiveId}
            statusFilter={statusFilter}
            onStatusFilter={setStatusFilter}
            channelFilter={channelFilter}
            onChannelFilter={setChannelFilter}
            searchTerm={searchTerm}
            onSearch={setSearchTerm}
            isRightColumn
          />
        </div>
      </div>
    </div>
  );
}
